import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx } from "vue"

const _hoisted_1 = {
  class: "flex flex-col gap-5 text-center",
  "data-test-id": "combined_policy_modal"
}
const _hoisted_2 = ["disabled", "data-test-id"]

import { computed, ref, watch } from 'vue';
import {
  Coverage,
  getInsuranceTypeDisplayName,
  InsuranceQuoteType,
  IRadioButtonCard,
  PetCoverage,
  QuoteTypeCodes,
  ResidentialCoverage,
  VehicleCoverage,
} from '@/interfaces';
import { Field, RichText } from '@sitecore-jss/sitecore-jss-vue';
import { ImageFieldValue } from '@sitecore-jss/sitecore-jss-vue/types/components/Image';
import RadioCardList from '../RadioCardList/RadioCardList.vue';
import { useI18n } from 'vue-i18n';
import InformationBox from '../InformationBox/InformationBox.vue';

export interface ISelectCoverageOption {
  coverages: Coverage[];
  modelValue: string;
  fields: {
    title: Field<string>;
    subtitle: Field<string>;
    homeIcon?: Field<ImageFieldValue>;
    homeContentsIcon?: Field<ImageFieldValue>;
    contentsIcon: Field<ImageFieldValue>;
    motorIcon?: Field<ImageFieldValue>;
    motorThirdPartyIcon?: Field<ImageFieldValue>;
    motorThirdPartyFireIcon: Field<ImageFieldValue>;
    boatIcon?: Field<ImageFieldValue>;
    caravanIcon?: Field<ImageFieldValue>;
    motorcycleIcon?: Field<ImageFieldValue>;
    petIcon?: Field<ImageFieldValue>;
    modalBtnText?: Field<string>;
  };
  disableCoverageCodes?: string[];
  disabledCoveragesMessage?: any;
  dataTestId?: string;
}

export default /*@__PURE__*/_defineComponent({
  __name: 'SelectCoverageOption',
  props: {
    coverages: {},
    modelValue: {},
    fields: {},
    disableCoverageCodes: {},
    disabledCoveragesMessage: {},
    dataTestId: {}
  },
  emits: ['update:modelValue', 'buttonClicked'],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const selectCoverageForm = ref<any>(null);

const emit = __emit;

const translation = useI18n();
const dictionary: Record<string, string> = translation.getLocaleMessage('en') as any;

const selectedCoverage = ref('');

watch(selectedCoverage, (newVal) => {
  if (newVal) {
    emit('update:modelValue', newVal);
  }
});

watch(props, (newVal) => {
  //Clear selection when re-opening modal.
  if (!newVal.modelValue && selectedCoverage.value) {
    selectedCoverage.value = '';

    //Clear validation when re-opening.
    const node = selectCoverageForm.value?.node;
    if (node) {
      node.reset();
    }
  }
});

const buttonClicked = () => {
  emit('buttonClicked');
};

const hasDisabled = computed(() => {
  return radioOptions?.value?.some((x) => x.disabled);
});

const coverageBindings = computed(() => {
  if (props.coverages?.length) {
    return {
      name: 'selectcoverage',
      id: 'selectCoverage',
      optionsClass: 'racq-max-w-2xl !mt-0',
      options: radioOptions.value,
      isComparison: true,
      validation: [['required']],
      validationMessages: {
        required: 'Please select a coverage.',
      },
    };
  }
  return undefined;
});

const radioOptions = computed((): IRadioButtonCard[] =>
  props.coverages.map(
    (x: Coverage): IRadioButtonCard => ({
      label: getInsuranceTypeDisplayName(dictionary, x?.coverageCode),
      help: getSubtitle(x),
      image: { value: getProductTypeIcon(x.coverageCode) },
      value: x.id,
      disabled: (props?.disableCoverageCodes ?? [])?.indexOf(x?.coverageCode) > -1,
    })
  )
);

const getProductTypeIcon = (coverageCode?: string): ImageFieldValue | undefined => {
  if (coverageCode) {
    switch (coverageCode) {
      case QuoteTypeCodes[InsuranceQuoteType.Contents]:
        return props.fields.contentsIcon?.value;
      case QuoteTypeCodes[InsuranceQuoteType.Home]:
        return props.fields.homeIcon?.value;
      case QuoteTypeCodes[InsuranceQuoteType.HomeAndContents]:
        return props.fields.homeContentsIcon?.value;
      case QuoteTypeCodes[InsuranceQuoteType.MotorComprehensive]:
        return props.fields.motorIcon?.value;
      case QuoteTypeCodes[InsuranceQuoteType.MotorFireTheftAndThirdPartyLiability]:
        return props.fields.motorThirdPartyFireIcon?.value;
      case QuoteTypeCodes[InsuranceQuoteType.MotorThirdPartyLiability]:
        return props.fields.motorThirdPartyIcon?.value;
      case QuoteTypeCodes[InsuranceQuoteType.PetComp]:
        return props.fields.petIcon?.value;
      case QuoteTypeCodes[InsuranceQuoteType.BoatComprehensive]:
      case QuoteTypeCodes[InsuranceQuoteType.BoatThirdParty]:
        return props.fields.boatIcon?.value;
      case QuoteTypeCodes[InsuranceQuoteType.Caravan]:
        return props.fields.caravanIcon?.value;
      case QuoteTypeCodes[InsuranceQuoteType.Motorcycle]:
        return props.fields.motorcycleIcon?.value;
    }
  }
  return props.fields.motorIcon?.value;
};

const getSubtitle = (coverage: Coverage): string => {
  if (coverage?.isHome) {
    return (coverage as ResidentialCoverage)?.riskAddress?.formattedLong;
  } else if (coverage?.isPet) {
    return `${(coverage as PetCoverage)?.petName}, ${(coverage as PetCoverage)?.petBreed}`;
  } else {
    return (coverage as VehicleCoverage)?.vehicle?.formattedSummary;
  }
};

return (_ctx: any,_cache: any) => {
  const _component_FormKit = _resolveComponent("FormKit")!

  return (_openBlock(), _createBlock(_component_FormKit, {
    ref_key: "selectCoverageForm",
    ref: selectCoverageForm,
    name: "selectCoverageForm",
    type: "form",
    actions: false,
    onSubmit: buttonClicked
  }, {
    default: _withCtx(({ state: { valid } }) => [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(_unref(RichText), {
          field: _ctx.fields.title,
          class: "text-lg-bold mt-4 mb-1 px-8"
        }, null, 8, ["field"]),
        _createVNode(_unref(RichText), {
          field: _ctx.fields.subtitle,
          class: "text-m px-0 md:px-12"
        }, null, 8, ["field"]),
        _createVNode(RadioCardList, {
          modelValue: selectedCoverage.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedCoverage).value = $event)),
          bindings: coverageBindings.value
        }, null, 8, ["modelValue", "bindings"]),
        (_ctx.disabledCoveragesMessage && hasDisabled.value)
          ? (_openBlock(), _createBlock(InformationBox, {
              key: 0,
              hideIcon: true,
              description: _ctx.disabledCoveragesMessage?.content?.value,
              type: _ctx.disabledCoveragesMessage?.messageType?.value
            }, null, 8, ["description", "type"]))
          : _createCommentVNode("", true),
        _createElementVNode("button", {
          disabled: !valid,
          "data-test-id": `${_ctx.dataTestId}_NextButton`,
          class: "btn btn-primary btn-md mt-5"
        }, _toDisplayString(_ctx.fields.modalBtnText?.value), 9, _hoisted_2)
      ])
    ]),
    _: 1
  }, 512))
}
}

})